// Use the same versioned URL as the layout, so the browser initializes motion once.
const { flyToCart, initMotion } = await import(document.querySelector('script[data-theme-motion]').dataset.themeMotion);

/** Línea para /cart/add.js desde el form de la tarjeta o del hero: variante elegida (swatches) y propiedades de línea
 * (preventa: «Entrega: preventa, llega el …», snippets/preventa-property.liquid). */
function quickBuyItem(form) {
  const data = new FormData(form);
  const item = { id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1 };
  const properties = {};
  for (const [name, value] of data) {
    const match = name.match(/^properties\[(.+)\]$/);
    if (match && value) properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;
  return item;
}

class QuickBuy extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.querySelector('form')?.addEventListener('submit', event => this.add(event), { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  async add(event) {
    event.preventDefault();
    const button = this.querySelector('button');
    if (button.disabled) return;
    const status = this.querySelector('[role=status]');
    const form = event.currentTarget;
    const original = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = this.dataset.adding;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [quickBuyItem(form)] }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || this.dataset.error);
      status.textContent = this.dataset.added;
      document.dispatchEvent(new CustomEvent('cart:items-added', { detail: { source: 'quick-buy', items: body.items } }));
      void flyToCart(this.closest('.product-card')?.querySelector('.product-card__image img'));
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) await drawer.open(true);
      else window.location.assign(`${window.Shopify?.routes?.root ?? '/'}cart`);
    } catch (error) {
      status.textContent = error.message || this.dataset.error;
    } finally {
      button.innerHTML = original;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('quick-buy')) customElements.define('quick-buy', QuickBuy);

// Swatches de la tarjeta (snippets/product-card.liquid): la variante elegida cambia la foto, el precio, el tachado y el id del
// quick-buy sin salir de la tarjeta; sin stock, el botón queda deshabilitado con «Agotado».
document.addEventListener('change', (event) => {
  const swatch = event.target.closest('[data-swatch]');
  const card = swatch?.closest('.product-card');
  if (!swatch || !card) return;
  const image = card.querySelector('.product-card__image img');
  if (image && swatch.dataset.image && image.currentSrc !== swatch.dataset.image) {
    image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.src = swatch.dataset.image;
  }
  const price = card.querySelector('[data-swatch-price]');
  if (price && swatch.dataset.price) price.textContent = swatch.dataset.price;
  const compare = card.querySelector('[data-swatch-compare]');
  if (compare) { compare.hidden = !swatch.dataset.compare; compare.querySelector('[data-swatch-compare-text]').textContent = swatch.dataset.compare || ''; }
  const transfer = card.querySelector('[data-transfer-price]'); // T-041: precio por transferencia (pack de 1) de esa variante
  if (transfer && swatch.dataset.transfer) transfer.textContent = swatch.dataset.transfer;
  const id = card.querySelector('[data-swatch-id]');
  if (id) id.value = swatch.value;
  syncSpin(card, swatch);
  const button = card.querySelector('[data-swatch-button]');
  const quick = button?.closest('quick-buy');
  if (button) {
    const available = swatch.dataset.available !== 'false';
    button.disabled = !available;
    const text = button.querySelector('[data-swatch-button-text]');
    if (text) {
      if (!text.dataset.add) text.dataset.add = text.textContent;
      text.textContent = available ? text.dataset.add : quick?.dataset.soldOut || text.dataset.add;
    }
  }
});

// Tarjetas que giran (ADR 0012): con hover fino y sin movimiento reducido, tras 250 ms sobre una tarjeta con 3D se descarga
// (una sola vez por archivo) el sprite de giro de la variante elegida y reemplaza la foto con una animación por cuadros; al
// salir vuelve la foto. Los swatches cambian el sprite (data-spins, por título de variante). En touch no hay giro: sello «3D».
const spinImages = new Map();
function loadSpin(url) {
  if (!spinImages.has(url)) {
    spinImages.set(url, new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ url, frames: Math.max(1, Math.round(img.naturalWidth / (img.naturalHeight * 1600 / 1300))) });
      img.onerror = () => reject(new Error(url));
      img.src = url;
    }));
  }
  return spinImages.get(url);
}
function syncSpin(card, swatch) {
  const spin = card.querySelector('[data-spin]');
  if (!spin) return;
  let spins = null;
  try { spins = JSON.parse(spin.dataset.spins || 'null'); } catch { spins = null; }
  const title = swatch?.closest('label')?.querySelector('.swatch__name')?.textContent.trim();
  const url = spins && title && spins[title] ? spins[title] : spin.dataset.spin;
  if (url && url !== spin.dataset.spin) { spin.dataset.spin = url; if (card.classList.contains('product-card--spinning')) showSpin(card, spin); }
}
function showSpin(card, spin) {
  const url = spin.dataset.spin;
  loadSpin(url).then(({ frames }) => {
    if (!card.spinHover || spin.dataset.spin !== url) return;
    spin.style.setProperty('--spin-frames', String(frames));
    spin.style.backgroundSize = `${frames * 100}% 100%`;
    spin.style.backgroundImage = `url("${url}")`;
    card.classList.add('product-card--spinning');
  }).catch(() => {});
}
if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('pointerenter', (event) => {
    const card = event.target instanceof Element && event.target.matches('.product-card--3d') ? event.target : null;
    if (!card || reduced.matches) return;
    const spin = card.querySelector('[data-spin]');
    if (!spin) return;
    card.spinHover = true;
    clearTimeout(card.spinTimer);
    card.spinTimer = setTimeout(() => showSpin(card, spin), 250);
  }, true);
  document.addEventListener('pointerleave', (event) => {
    const card = event.target instanceof Element && event.target.matches('.product-card--3d') ? event.target : null;
    if (!card) return;
    card.spinHover = false;
    clearTimeout(card.spinTimer);
    card.classList.remove('product-card--spinning');
  }, true);
}

// Comparar en 3D desde las tarjetas (ADR 0012, parte C): hasta dos productos (handle y color elegido) en localStorage; con dos,
// una barra flotante lleva al comparador con ?a=…&b=… y el resto de los checks queda deshabilitado.
const COMPARE_KEY = 'compare3d';
function compareList() {
  try { const list = JSON.parse(localStorage.getItem(COMPARE_KEY) || '[]'); return Array.isArray(list) ? list.filter((i) => i && i.handle).slice(0, 2) : []; } catch { return []; }
}
function saveCompare(list) { try { localStorage.setItem(COMPARE_KEY, JSON.stringify(list)); } catch { /* sin storage */ } }
function syncCompare() {
  const checks = [...document.querySelectorAll('[data-compare-check]')];
  if (!checks.length) return;
  const list = compareList();
  checks.forEach((check) => { const on = list.some((i) => i.handle === check.value); check.checked = on; check.disabled = !on && list.length >= 2; });
  let bar = document.querySelector('[data-compare-bar]');
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'compare-bar';
    bar.setAttribute('data-compare-bar', '');
    bar.hidden = true;
    const link = document.createElement('a');
    link.className = 'button compare-bar__link';
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'compare-bar__clear';
    clear.textContent = checks[0].dataset.compareClear || '';
    clear.addEventListener('click', () => { saveCompare([]); syncCompare(); });
    bar.append(link, clear);
    document.body.append(bar);
  }
  const link = bar.querySelector('a');
  const url = checks[0].dataset.compareUrl || '';
  if (list.length === 2 && url) {
    const query = list.map((item, k) => `${'ab'[k]}=${encodeURIComponent(item.variant ? `${item.handle}:${item.variant}` : item.handle)}`).join('&');
    link.href = `${url}${url.includes('?') ? '&' : '?'}${query}`;
    link.textContent = checks[0].dataset.compareCta || '';
    bar.hidden = false;
  } else bar.hidden = true;
}
document.addEventListener('change', (event) => {
  const check = event.target.closest?.('[data-compare-check]');
  if (!check) return;
  const list = compareList().filter((i) => i.handle !== check.value);
  if (check.checked) {
    if (list.length >= 2) { check.checked = false; return; }
    const swatch = check.closest('.product-card')?.querySelector('[data-swatch]:checked');
    const variant = swatch?.closest('label')?.querySelector('.swatch__name')?.textContent.trim() || '';
    list.push({ handle: check.value, variant });
  }
  saveCompare(list);
  syncCompare();
});
syncCompare();
document.addEventListener('shopify:section:load', syncCompare);

function initFilters(root = document) {
  root.querySelectorAll('.catalog:not(.catalog--enhanced)').forEach(catalog => {
    const form = catalog.querySelector('.catalog__form');
    const open = catalog.querySelector('[data-filter-open]');
    const close = catalog.querySelector('[data-filter-close]');
    if (!form || !open || !close) return;
    catalog.classList.add('catalog--enhanced');
    const mobile = matchMedia('(max-width: 899px)');
    function hide(restore = true) {
      catalog.classList.remove('catalog--filtering');
      form.removeAttribute('role');
      form.removeAttribute('aria-modal');
      form.removeAttribute('aria-label');
      open.setAttribute('aria-expanded', 'false');
      if (restore) open.focus();
    }
    open.addEventListener('click', () => {
      catalog.classList.add('catalog--filtering');
      form.setAttribute('role', 'dialog');
      form.setAttribute('aria-modal', 'true');
      form.setAttribute('aria-label', open.textContent.trim());
      open.setAttribute('aria-expanded', 'true');
      close.focus();
    });
    close.addEventListener('click', () => hide());
    mobile.addEventListener('change', () => hide(false));
    form.addEventListener('keydown', event => {
      if (!mobile.matches || !catalog.classList.contains('catalog--filtering')) return;
      if (event.key === 'Escape') { event.preventDefault(); hide(); }
      if (event.key !== 'Tab') return;
      const controls = [...form.querySelectorAll('a,button,input,select,summary')].filter(el => !el.disabled && el.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  });
}
initFilters();
document.addEventListener('shopify:section:load', event => initFilters(event.target));
document.addEventListener('cart:fly', event => { void flyToCart(event.detail?.image); });
document.addEventListener('cart:rendered', event => initMotion(event.detail?.root || document));

// Barra de anuncio: los mensajes rotan con fundido cada 4 s; pausa con hover, foco o botón; estática con movimiento reducido.
class AnnounceBar extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.items = [...this.querySelectorAll('.announce__item')];
    this.button = this.querySelector('[data-announce-pause]');
    this.index = Math.max(0, this.items.findIndex(item => item.classList.contains('is-active')));
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (this.items.length < 2) return;
    this.classList.add('announce--ready');
    if (this.button) {
      this.button.hidden = false;
      this.button.addEventListener('click', () => {
        this.paused = !this.paused;
        this.button.setAttribute('aria-pressed', String(this.paused));
        this.schedule();
      }, { signal });
    }
    this.addEventListener('pointerenter', () => { this.hover = true; }, { signal });
    this.addEventListener('pointerleave', () => { this.hover = false; this.schedule(); }, { signal });
    this.addEventListener('focusin', () => { this.hover = true; }, { signal });
    this.addEventListener('focusout', () => { this.hover = false; this.schedule(); }, { signal });
    this.reduced.addEventListener('change', () => this.schedule(), { signal });
    this.schedule();
  }
  disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); this.controller = null; }
  schedule() {
    clearTimeout(this.timer);
    if (this.paused || this.hover || this.reduced.matches || document.hidden) return;
    this.timer = setTimeout(() => this.next(), 4000);
  }
  next() {
    if (this.paused || this.hover || this.reduced.matches) return;
    const current = this.items[this.index];
    this.index = (this.index + 1) % this.items.length;
    const nextItem = this.items[this.index];
    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');
    nextItem.classList.add('is-active');
    nextItem.removeAttribute('aria-hidden');
    this.schedule();
  }
}
if (!customElements.get('announce-bar')) customElements.define('announce-bar', AnnounceBar);
document.addEventListener('visibilitychange', () => {
  document.querySelectorAll('announce-bar').forEach(bar => { clearTimeout(bar.timer); if (!document.hidden) bar.schedule(); });
});

// Carrusel horizontal con scroll-snap nativo; las flechas solo aparecen con JS.
function initCarousels(root = document) {
  root.querySelectorAll('[data-carousel]:not(.carousel--ready)').forEach(carousel => {
    const track = carousel.querySelector('[data-carousel-track]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    carousel.classList.add('carousel--ready');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const step = () => Math.max(track.clientWidth * 0.8, 200);
    const update = () => {
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      const scrollable = track.scrollWidth > track.clientWidth + 2;
      prev.hidden = !scrollable;
      next.hidden = !scrollable;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduced.matches ? 'auto' : 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduced.matches ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  });
}
initCarousels();
document.addEventListener('shopify:section:load', event => initCarousels(event.target));

// Tira de fotos: el loop solo se activa con JS y sin movimiento reducido; el botón pausa el track.
/** Fotos del hero (T-036): Liquid imprime los dos carriles (el 2 con las fotos en orden inverso, aria-hidden y sin foco); acá
 * se agrega la segunda copia de cada grupo para el loop sin salto (aria-hidden, links sin foco). Si un carril viene vacío con
 * data-lane-mirror (marcado anterior), se arma con las fotos del carril 1 en orden inverso. */
function buildHeroLanes(strip) {
  if (strip.dataset.lanesBuilt || !strip.hasAttribute('data-hero-photos')) return;
  strip.dataset.lanesBuilt = 'true';
  const group = strip.querySelector('.hero__lane--1 .hero__lane-group');
  const mirror = strip.querySelector('[data-lane-mirror]');
  if (!group) return;
  const silent = (ul, hidden) => {
    ul.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    ul.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
    if (hidden) ul.setAttribute('aria-hidden', 'true');
    return ul;
  };
  strip.querySelectorAll('.hero__lane-track').forEach((track) => {
    const own = track.querySelector('.hero__lane-group');
    if (own) track.append(silent(own.cloneNode(true), true));
  });
  if (!mirror || mirror.children.length) return;
  const reversed = group.cloneNode(false);
  [...group.children].reverse().forEach((li) => reversed.append(li.cloneNode(true)));
  mirror.append(silent(reversed, false), silent(reversed.cloneNode(true), true));
}

/** Fotos del hero con src en data-hero-* (ADR 0016): desde 1280 px se ponen ya (columnas junto al 3D); hasta 1279 px, en
 * inactividad después de load, así no compiten con el póster del visor (LCP mobile). Incluye las copias del loop. */
function wakeHeroPhotos(strip) {
  const wake = () => strip.querySelectorAll('img[data-hero-src]').forEach((img) => {
    if (img.dataset.heroSrcset) img.srcset = img.dataset.heroSrcset;
    img.src = img.dataset.heroSrc;
    delete img.dataset.heroSrc;
    delete img.dataset.heroSrcset;
  });
  if (matchMedia('(min-width: 1280px)').matches) return wake();
  const idle = () => { if ('requestIdleCallback' in window) requestIdleCallback(wake, { timeout: 3000 }); else setTimeout(wake, 1000); };
  if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
}

function initStrips(root = document) {
  root.querySelectorAll('[data-strip]:not(.strip--ready)').forEach(strip => {
    buildHeroLanes(strip);
    wakeHeroPhotos(strip);
    const button = strip.querySelector('[data-strip-pause]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      strip.classList.toggle('strip--ready', !reduced.matches);
      if (button) button.hidden = reduced.matches;
    };
    button?.addEventListener('click', () => {
      const paused = strip.classList.toggle('strip--paused');
      button.setAttribute('aria-pressed', String(paused));
    });
    reduced.addEventListener('change', apply);
    apply();
  });
}
initStrips();
document.addEventListener('shopify:section:load', event => initStrips(event.target));
